import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { View } from "react-native";
import { TAG_BOUNDING_BOX } from "src/api/featureFlags";
import Profile from "src/components/Profile";
import log from "src/helpers/log";
import useInitMemo from "src/hooks/useInitMemo";
import useStyle from "src/hooks/useStyle";
import { color } from "src/styles";
import { radiusMedium } from "src/styles/radius";
import RenderTag from "./components/RenderTag";
import generateTagPlacements from "./helpers/generateTagPlacements";
import parseTagsOverlayPhoto from "./helpers/parseTagsOverlayPhoto";
import { HandleTagSettled, TagsOverlayProps, TagsOverlayTag } from "./types";

const TagsOverlayContent = ({
	photo: photoInput,
	photoScreenHeight,
	photoScreenWidth,
	displayBoundingBox,
}: TagsOverlayProps) => {
	const { theme } = useStyle();
	const photo = useMemo(() => parseTagsOverlayPhoto(photoInput), [photoInput]);
	const tagLookup = useRef<Record<string, TagsOverlayTag>>(
		useInitMemo(() => {
			const lookup: Record<string, TagsOverlayTag> = {};
			for (const tag of photo.tags) {
				lookup[tag.curKey] = tag;
			}
			return lookup;
		}, []),
	);

	useEffect(() => {
		for (const tag of photo.tags) {
			if (!tagLookup.current[tag.curKey]) {
				tagLookup.current[tag.curKey] = tag;
			}
		}

		// If a tag is removed then we re-render all tags
		const curTagKeys = new Set(photo.tags.map((tag) => tag.curKey));
		for (const tag of Object.keys(tagLookup.current)) {
			if (!curTagKeys.has(tag)) {
				const newLookup: Record<string, TagsOverlayTag> = {};
				for (const tag of photo.tags) {
					newLookup[tag.curKey] = tag;
				}
				tagLookup.current = newLookup;
				setTags(Object.values(newLookup));
				break;
			}
		}
	}, [photo.tags]);

	const [tags, setTags] = useState<TagsOverlayTag[]>(photo.tags);
	const handleTagSettled = useCallback<HandleTagSettled>(
		(key, labelDim) => {
			const tag = tagLookup.current[key];
			if (!tag) {
				log.error("Tag not found");
				return;
			}
			tag.didSettle = true;
			if (labelDim) tag.labelDim = labelDim;

			const curTags = Object.values(tagLookup.current);
			if (curTags.every((tag) => tag.didSettle && tag.labelDim)) {
				setTags(
					generateTagPlacements(curTags, {
						photoScreenWidth,
						photoScreenHeight,
					}),
				);
			}
		},
		[photoScreenHeight, photoScreenWidth],
	);

	return (
		<View
			style={{
				width: photoScreenWidth,
				height: photoScreenHeight,
			}}
			pointerEvents="box-none"
		>
			{tags.map((tag) => {
				return (
					<Fragment key={`${tag.userId}-${tag.x}-${tag.y}`}>
						{(displayBoundingBox || TAG_BOUNDING_BOX) && (
							<View
								style={{
									position: "absolute",
									left: tag.x * photoScreenWidth,
									top: tag.y * photoScreenHeight,
									width: tag.width * photoScreenWidth,
									height: tag.height * photoScreenHeight,
									borderWidth: 1,
									borderColor: tag.__debugColor ?? color.Text[theme],
									borderRadius: radiusMedium,
								}}
							/>
						)}
						<Profile
							userId={tag.userId}
							onProfileUnavailable={() => handleTagSettled(tag.curKey)}
							style={[
								{
									position: "absolute",
								},
								tag.labelPos
									? {
											top: tag.labelPos.y,
											left: tag.labelPos.x,
										}
									: {
											top: -100000,
											left: -100000,
										},
							]}
							renderContent={(profile) => {
								return (
									<RenderTag
										{...profile}
										handleTagSettled={handleTagSettled}
										tag={tag}
									/>
								);
							}}
						/>
					</Fragment>
				);
			})}
		</View>
	);
};

const TagsOverlay = (props: TagsOverlayProps) => {
	return (
		<TagsOverlayContent
			key={`${props.photoScreenWidth}-${props.photoScreenHeight}`}
			{...props}
		/>
	);
};

export default memo(TagsOverlay);
